import type { RootState } from 'store';

/**
 * Every `getEventDetails` cache key the event may be stored under (HNS-238, widened in HNS-148).
 *
 * The cache is keyed by the exact arg the page fetched with, and that key is type-sensitive:
 * `{"id":"123"}` is a different entry from `{"id":123}`, and a UUID publicId is a third. The
 * details/join pages pass the raw route param (`useActiveEventDetails`) while the live-meeting
 * routes and the WS payloads carry the numeric id, so a patch written against one shape silently
 * misses the entry the page is actually reading — which is why the Join button stayed disabled
 * until a manual refresh.
 *
 * `activeEventFetchId` is the fetch key verbatim (`meta.arg.originalArgs.id`, original type
 * preserved). `activePublicId` must be supplied by the caller rather than selected here: this module
 * is re-exported through `./utils`, which `eventApi` imports, so importing `../selectors` (which
 * imports `eventApi`) would close an import cycle.
 *
 * A patch against a key with no entry is a harmless no-op, so returning a superset is safe.
 */
export const getEventDetailsCacheIds = (
  state: RootState,
  eventId: number | string,
  activePublicId?: string
): (string | number)[] => {
  const { activeEventId, activeEventFetchId } = state.event;
  const isActiveEvent =
    eventId === activeEventId ||
    eventId === activeEventFetchId ||
    (activePublicId != null && eventId === activePublicId);

  if (!isActiveEvent) {
    return [eventId];
  }

  return [...new Set<string | number>([eventId, activeEventId, activeEventFetchId].filter((id) => id != null))];
};
